import React, { useEffect, useState } from "react";
import BodyLoading from "../../../components/Auth/BodyLoading/BodyLoading";
import { SubErrorModal } from "../../../components/Modals/SubErrorModal/SubErrorModal";
import { history } from "../../../core/helpers/history";
import { router } from "../../../core/helpers/router";
import {
  getStorage,
  storageSubscriptionPeriod
} from "../../../core/helpers/storage";
import { Utilities } from "../../../core/helpers/utilities";
import { ErrorDto } from "../../../core/models/dtos/error.dto";
import { PaymentCardDto } from "../../../core/models/dtos/paymentCard.dto";
import PaymentService from "../../../core/services/payment.service";
import PaymentIframe from "../PaymentIframe/PaymentIframe";
import "./CardPaymentPage.scss";

interface CardFormData {
  cardNumber: string;
  cardOwner: string;
  expiryDate: string;
  cvc: string;
}

interface CardFormErrors {
  cardNumber: boolean;
  cardOwner: boolean;
  expiryDate: boolean;
  cvc: boolean;
}

interface StartPaymentRequest {
  ccOwner?: string;
  cardNumber?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cvv?: string;
  subscriptionType: string;
  cardId?: number;
}


const paymentService = new PaymentService();

const CardPaymentPage = () => {
  const [paymentCards, setPaymentCards] = useState<PaymentCardDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CardFormData>({
    cardNumber: "",
    cardOwner: "",
    expiryDate: "",
    cvc: "",
  });
  const [formErrors, setFormErrors] = useState<CardFormErrors>({
    cardNumber: false,
    cardOwner: false,
    expiryDate: false,
    cvc: false,
  });
  const [showForm, setShowForm] = useState<boolean>(false);
  const [paymentHtml, setPaymentHtml] = useState<string | null>(null);
  const [apiError, setApiError] = useState<ErrorDto>();
  const fetchCards = async () => {
    try {
      setIsLoading(true);
      const cards = await paymentService.getPaymentCards();
      setPaymentCards(cards);

      // Show form if no cards are returned
      setShowForm(cards.length === 0);

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching payment cards:", error);
      setIsLoading(false);
      setShowForm(true);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleCardSelection = (cardId: string) => {
    setSelectedCardId(cardId);
  };

  const handleDeleteCard = async (cardId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card selection when clicking delete

    if (isDeletingCard) return;

    try {
      setIsDeletingCard(true);
      await paymentService.deletePaymentCard(cardId);

      // Refresh the card list
      await fetchCards();

      // If the deleted card was selected, clear selection
      if (selectedCardId === cardId) {
        setSelectedCardId(null);
      }
    } catch (error) {
      console.error("Error deleting card:", error);
      const errorDto: ErrorDto = {
        message: "Kart silme sırasında bir hata oluştu.",
        path: "/payment/pay-tr/delete-card",
        status: 500,
      };
      setApiError(errorDto);
    } finally {
      setIsDeletingCard(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Clear the error when user starts typing
    setFormErrors({
      ...formErrors,
      [name]: false,
    });

    // Basic input validation and formatting
    if (name === "cardNumber") {
      // Only allow numbers and format as groups of 4
      const formattedValue = value
        .replace(/\D/g, "")
        .replace(/(.{4})/g, "$1 ")
        .trim();
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else if (name === "expiryDate") {
      // Format as MM/YY
      const digits = value.replace(/\D/g, "");
      let formattedValue = value;
      if (digits.length <= 2) {
        formattedValue = digits;
      } else {
        formattedValue = `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
      }
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else if (name === "cvc") {
      // Only allow up to 3 digits
      const formattedValue = value.replace(/\D/g, "").substring(0, 3);
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const getSubscriptionType = (): string => {
    // Get user's MSISDN from token
    const msisdn = Utilities.getMsisdnFromToken();

    // For non-Turkish MSISDN (abroad users), always use YEARLY subscription
    if (msisdn && !Utilities.isMsisdnTurkish(msisdn)) {
      return "YEARLY";
    }

    // Otherwise, get from local storage or use default
    const storedType = getStorage(storageSubscriptionPeriod);
    return storedType ? storedType.toUpperCase() : "MONTHLY";
  };

  const validateCardForm = (): boolean => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 1-12
    const currentYear = now.getFullYear() % 100; // 2-digit year

    let isValid = true;
    const newErrors = {
      cardNumber: false,
      cardOwner: false,
      expiryDate: false,
      cvc: false,
    };

    // Validate card number (must be 16 digits)
    if (
      !formData.cardNumber ||
      formData.cardNumber.replace(/\s/g, "").length !== 16
    ) {
      newErrors.cardNumber = true;
      isValid = false;
    }

    // Validate card owner
    if (!formData.cardOwner.trim()) {
      newErrors.cardOwner = true;
      isValid = false;
    }

    // Validate expiry date
    if (!formData.expiryDate || !formData.expiryDate.includes("/")) {
      newErrors.expiryDate = true;
      isValid = false;
    } else {
      // Check if date is in the past
      const [month, year] = formData.expiryDate.split("/").map(Number);
      const expYear = year + 2000; // Convert to 4-digit year

      if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
        newErrors.expiryDate = true;
        isValid = false;
      } else if (
        expYear < now.getFullYear() ||
        (expYear === now.getFullYear() && month < currentMonth)
      ) {
        newErrors.expiryDate = true;
        isValid = false;
      }
    }

    // Validate CVC
    if (!formData.cvc || formData.cvc.length < 3) {
      newErrors.cvc = true;
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const handleSubscribe = async () => {
    try {
      if (isSubmitting) return;

      setIsSubmitting(true);

      const subscriptionType = getSubscriptionType();
      const request: StartPaymentRequest = {
        subscriptionType: subscriptionType,
      };

      if (showForm) {
        // Card form submission
        if (!validateCardForm()) {
          setIsSubmitting(false);
          return;
        }

        // Parse expiry date
        const [month, year] = formData.expiryDate.split("/");

        request.ccOwner = formData.cardOwner;
        request.cardNumber = formData.cardNumber.replace(/\s/g, ""); // Remove spaces
        request.expiryMonth = month;
        request.expiryYear = year;
        request.cvv = formData.cvc;
      } else {
        // Card selection
        if (!selectedCardId) {
          setIsSubmitting(false);
          return;
        }

        request.cardId = parseInt(selectedCardId);
      }

      // Call the API
      const htmlResponse = await paymentService.startPayTrPayment(request);

      // Set the HTML content for the iframe
      setPaymentHtml(htmlResponse);
    } catch (error) {
      console.error("Error starting payment:", error);
      const errorDto: ErrorDto = {
        message: "Ödeme sırasında bir hata oluştu. Tekrar deneyiniz.",
        path: "/payment/pay-tr/start",
        status: 500,
      };
      setApiError(errorDto);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push(router.SUBSCRIPTION);
  };

  const handleCloseIframe = () => {
    setPaymentHtml(null);
  };

  const handleAddNewCard = () => {
    setShowForm(true);
  };

  const renderCardForm = () => (
    <div className="card-payment-form">
      <div className="form-fields">
        <div className="form-group">
          <input
            type="text"
            name="cardNumber"
            value={formData.cardNumber}
            onChange={handleInputChange}
            placeholder="Kart Numarası"
            className={`form-control ${formErrors.cardNumber ? "error" : ""}`}
            maxLength={19} // 16 digits + 3 spaces
          />
        </div>

        <div className="form-group">
          <input
            type="text"
            name="cardOwner"
            value={formData.cardOwner}
            onChange={handleInputChange}
            placeholder="İsim - Soyisim"
            className={`form-control ${formErrors.cardOwner ? "error" : ""}`}
          />
        </div>

        <div className="form-row">
          <div className="form-group date-field">
            <input
              type="text"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleInputChange}
              placeholder="Tarih"
              className={`form-control ${formErrors.expiryDate ? "error" : ""}`}
              maxLength={5} // MM/YY
            />
          </div>

          <div className="form-group cvc-field">
            <input
              type="text"
              name="cvc"
              value={formData.cvc}
              onChange={handleInputChange}
              placeholder="CVC"
              className={`form-control ${formErrors.cvc ? "error" : ""}`}
              maxLength={3}
            />
          </div>
        </div>

        <div className="form-buttons">
          <button
            type="button"
            className="btn subscribe-btn"
            onClick={handleSubscribe}
            disabled={isSubmitting}
          >
            Abone Ol
          </button>
          <button
            type="button"
            className="btn cancel-btn"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            İptal Et
          </button>
        </div>

        <div className="terms-text">
          Bu işlemi onaylayarak{" "}
          <a
            href="https://static.4play.com.tr/fener/fbtk-abonelik-sozlemesi.pdf"
            className="terms-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            abonelik koşullarını
          </a>{" "}
          da onaylamış olacaksınız.
        </div>
      </div>
    </div>
  );

  const renderCardList = () => (
    <div className="cards-container">
      <div className="cards-title-container">
        <h2 className="cards-title">Kayıtlı Kartlarım</h2>
      </div>

      <div className="cards-list">
        {paymentCards.map((card) => (
          <div key={card.id} className="card-item-container">
            <div
              className={`card-item ${
                selectedCardId === card.id ? "selected" : ""
              }`}
              onClick={() => handleCardSelection(card.id)}
            >
              <div className="card-info">
                <span className="card-holder">{card.cardOwner}</span>
                <span className="card-number">{card.cardNo}</span>
              </div>
            </div>
            <button
              className="delete-button"
              onClick={(e) => handleDeleteCard(card.id, e)}
              disabled={isDeletingCard}
            >
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.7862 2.2193H10.5365V1.75987C10.5365 0.789474 9.66776 0 8.69737 0H5.5329C4.5625 0 3.70821 0.789474 3.70821 1.75987V2.2193H0.444079C0.197369 2.2193 0 2.5 0 2.74671C0 2.99342 0.197369 3.19079 0.444079 3.19079H1.11499V13.625C1.11499 14.9342 2.3125 16 3.62171 16H10.6086C11.9178 16 13.115 14.9342 13.115 13.625V3.19079H13.7862C14.0329 3.19079 14.2303 2.99342 14.2303 2.74671C14.2303 2.5 14.0329 2.2193 13.7862 2.2193ZM4.75499 1.75987C4.75499 1.27961 5.05263 1 5.5329 1H8.69737C9.17763 1 9.46479 1.27961 9.46479 1.75987V2.2193H4.75499V1.75987ZM12.0954 13.625C12.0954 14.4441 11.4341 15 10.615 15H3.61499C2.79591 15 2.13487 14.4441 2.13487 13.625V3.19079H12.0987V13.625H12.0954Z"
                  fill="#BDBCBC"
                />
                <path
                  d="M7.17097 13.5197C7.44875 13.5197 7.67097 13.3223 7.67097 13.0756V5.22692C7.67097 4.98021 7.44875 4.78284 7.17097 4.78284C6.8932 4.78284 6.67097 4.98021 6.67097 5.22692V13.0723C6.67097 13.319 6.8932 13.5197 7.17097 13.5197Z"
                  fill="#BDBCBC"
                />
                <path
                  d="M4.27277 13.0297C4.55054 13.0297 4.77277 12.8323 4.77277 12.5856V5.71385C4.77277 5.46714 4.55054 5.26978 4.27277 5.26978C3.99499 5.26978 3.77277 5.46714 3.77277 5.71385V12.5856C3.77277 12.8323 3.99869 13.0297 4.27277 13.0297Z"
                  fill="#BDBCBC"
                />
                <path
                  d="M10.069 13.0297C10.3468 13.0297 10.569 12.8323 10.569 12.5856V5.71385C10.569 5.46714 10.3468 5.26978 10.069 5.26978C9.79121 5.26978 9.56898 5.46714 9.56898 5.71385V12.5856C9.56898 12.8323 9.79121 13.0297 10.069 13.0297Z"
                  fill="#BDBCBC"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>

      <div className="card-actions">
        <button
          type="button"
          className="btn add-card-btn"
          onClick={handleAddNewCard}
        >
          <span className="add-icon">
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.95237 5.3153H5.31527V6.9524C5.31527 7.12643 5.17403 7.26767 5 7.26767C4.82597 7.26767 4.68472 7.12643 4.68472 6.9524V5.3153H3.04762C2.87359 5.3153 2.73235 5.17406 2.73235 5.00003C2.73235 4.826 2.87359 4.68476 3.04762 4.68476H4.68472V3.04766C4.68472 2.87363 4.82597 2.73238 5 2.73238C5.17403 2.73238 5.31527 2.87363 5.31527 3.04766V4.68476H6.95237C7.1264 4.68476 7.26764 4.826 7.26764 5.00003C7.26764 5.17406 7.1264 5.3153 6.95237 5.3153ZM5 0C2.23801 0 0 2.23801 0 5C0 7.76199 2.23801 10 5 10C7.76199 10 10 7.76199 10 5C9.99748 2.23969 7.76031 0.00252217 5 0Z"
                fill="#202046"
              />
            </svg>
          </span>{" "}
          Yeni Kart Ekle
        </button>

        <button
          type="button"
          className="btn subscribe-btn"
          onClick={handleSubscribe}
          disabled={!selectedCardId || isSubmitting}
        >
          Abone Ol
        </button>
      </div>

      <div className="terms-text">
        Bu işlemi onaylayarak{" "}
        <a
          href="https://static.4play.com.tr/fener/fbtk-abonelik-sozlemesi.pdf"
          rel="noopener noreferrer"
          className="terms-link"
          target="_blank"
        >
          abonelik koşullarını
        </a>{" "}
        da onaylamış olacaksınız.
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="card-payment-loading">
        <BodyLoading />
      </div>
    );
  }

  // If we have HTML content, show the payment iframe
  if (paymentHtml) {
    return (
      <PaymentIframe htmlContent={paymentHtml} onClose={handleCloseIframe} />
    );
  }

  return (
    <div className="card-payment-page">
      {showForm ? renderCardForm() : renderCardList()}
      <SubErrorModal
        apiError={apiError}
        resetApiError={() => {
          setApiError(undefined);
        }}
      />
    </div>
  );
};

export default CardPaymentPage;
